import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiUrl } from '../../config/api';

interface Notification {
  id: string;
  type: 'order' | 'message' | 'appointment' | 'system' | 'payment';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  actionUrl?: string;
  priority: 'low' | 'medium' | 'high';
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Load notifications from API
  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(apiUrl('/api/notifications'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unreadCount || 0);
      } else {
        // Fallback to mock data if API fails
        loadMockNotifications();
      }
    } catch (_error) {
      console.error('Failed to load notifications:', error);
      loadMockNotifications();
    }
  };

  const loadMockNotifications = () => {
    const mockNotifications: Notification[] = [
      {
        id: '1',
        type: 'order',
        title: 'Order Update',
        message: 'Your order #12345 has been completed and is ready for pickup.',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        read: false,
        actionUrl: '/orders',
        priority: 'high'
      },
      {
        id: '2',
        type: 'message',
        title: 'New Message',
        message: 'You have a new message from your tailor.',
        timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
        read: false,
        actionUrl: '/messaging',
        priority: 'medium'
      },
      {
        id: '3',
        type: 'appointment',
        title: 'Appointment Reminder',
        message: 'Your fitting appointment is scheduled for tomorrow at 2 PM.',
        timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
        read: true,
        actionUrl: '/appointments',
        priority: 'medium'
      },
      {
        id: '4',
        type: 'system',
        title: 'System Update',
        message: 'New features have been added to your dashboard.',
        timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        read: true,
        priority: 'low'
      },
      {
        id: '5',
        type: 'payment',
        title: 'Payment Confirmed',
        message: 'Your payment of $150 has been processed successfully.',
        timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
        read: true,
        actionUrl: '/orders',
        priority: 'high'
      }
    ];
    
    setNotifications(mockNotifications);
    setUnreadCount(mockNotifications.filter(n => !n.read).length);
  };

  const addNotification = (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    const newNotification: Notification = {
      ...notification,
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      read: false
    };

    setNotifications(prev => [newNotification, ...prev]);
    setUnreadCount(prev => prev + 1);

    // Show browser notification if permission granted
    if (Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: notification.message,
        icon: '/favicon.ico'
      });
    }
  };

  const markAsRead = async (id: string) => {
    setNotifications(prev => 
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));

    // Update on server
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        await fetch(apiUrl(`/api/notifications/${id}/read`), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (_error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);

    // Update on server
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        await fetch(apiUrl('/api/notifications/mark-all-read'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (_error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const removeNotification = async (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
    setUnreadCount(prev => {
      const notification = notifications.find(n => n.id === id);
      return notification && !notification.read ? prev - 1 : prev;
    });

    // Update on server
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        await fetch(apiUrl(`/api/notifications/${id}`), {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (_error) {
      console.error('Failed to remove notification:', error);
    }
  };

  const clearAllNotifications = async () => {
    setNotifications([]);
    setUnreadCount(0);

    // Update on server
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        await fetch(apiUrl('/api/notifications/clear-all'), {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (_error) {
      console.error('Failed to clear all notifications:', error);
    }
  };

  // Request notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAllNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}

// Utility functions for creating notifications
export const createOrderNotification = (orderId: string, status: string) => ({
  type: 'order' as const,
  title: 'Order Update',
  message: `Your order #${orderId} status has been updated to: ${status}`,
  actionUrl: `/orders/${orderId}`,
  priority: 'high' as const
});

export const createMessageNotification = (senderName: string) => ({
  type: 'message' as const,
  title: 'New Message',
  message: `You have a new message from ${senderName}`,
  actionUrl: '/messaging',
  priority: 'medium' as const
});

export const createAppointmentNotification = (appointmentTime: string) => ({
  type: 'appointment' as const,
  title: 'Appointment Reminder',
  message: `Your appointment is scheduled for ${appointmentTime}`,
  actionUrl: '/appointments',
  priority: 'medium' as const
});

export const createSystemNotification = (message: string) => ({
  type: 'system' as const,
  title: 'System Update',
  message,
  priority: 'low' as const
});

export const createPaymentNotification = (amount: number, status: string) => ({
  type: 'payment' as const,
  title: 'Payment Update',
  message: `Your payment of $${amount} has been ${status}`,
  actionUrl: '/orders',
  priority: 'high' as const
});
